import './mask-field.scss'

import { defineComponent } from 'vue'
import _ from 'lodash'
import { first } from 'rxjs/operators'
import { faEraser, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons'

import { EntityField, EntityFieldStatus } from '@skinsoft/types'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { EditableTranslatedStringList, EntitySelect } from '@skinsoft/ui'
import { MaskFieldText } from '../mask-field-text'
import { MaskFieldBoolean } from '../mask-field-boolean'
import { MaskFieldDate } from '../mask-field-date'
import { MaskFieldRef } from '../mask-field-ref'
import { MaskFieldListRef } from '../mask-field-list-ref'
import { MaskFieldListTranslatedString } from '../mask-field-list-translated-string'

const lastId = 1;

export enum MaskFieldUpdateEventType {
    UPDATE = "UPDATE",
    DELETE = "DELETE",
}

export interface MaskFieldUpdateEvent {
    abstractEntity: string,
    fieldPath: string,
    value: any,
    type?: MaskFieldUpdateEventType,
}

const ENTITY_FIELDS_REFERENCES_TYPES = [
    'Alias',
    'Archive',
    'ContainerItem',
    'Location',
    'ThesaurusTerm',
    'Specimen'
]

export const MaskField = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sEditableTranslatedStringList: EditableTranslatedStringList,
        sEntitySelect: EntitySelect,
        sMaskFieldText: MaskFieldText,
        sMaskFieldBoolean: MaskFieldBoolean,
        sMaskFieldDate: MaskFieldDate,
        sMaskFieldRef: MaskFieldRef,
        sMaskFieldListRef: MaskFieldListRef,
        sMaskFieldListTranslatedString: MaskFieldListTranslatedString,
    },
    props: [
        'entityField',
        'fieldValue',
        'isInMetaMask',
        'editing',
        'getStatus',
        'entityFields',
        'dataCy',
        'fieldValueSubWemi',
        'fieldValueItem',
        'fieldValueFromRoot',
    ],
    template: require('./mask-field.html'),
    data() {
        return {
            fieldLabel: "",

            value: null as any,

            // Icons
            eraseIcon: faEraser,
            plusIcon: faPlusCircle,
            trashIcon: faTrash,

            singleComplex: [
                'ActualContainerArchive',
                'ConservationNoteArchive',
                'CurrentItemState',
                'CurrentItemStateArchive',
                'MediumTypeArchive',
                'PermanentLocation',
                'PermanentLocationArchive',
                'RealTimeLocation',
                'RealTimeLocationArchive',
                'TypeOfPackaging',
            ],
        }
    },
    computed: {
        isReferenceField(): boolean {
            if(this.entityField.type.referenceField && this.entityField.type.type !== 'List') {
                return true
            }
            else if(this.entityField.type.type){
                return this.checkEntityFieldTypeIsReference(this.entityField.type.type)
            }

            return false
        },
        isListReferenceField(): boolean {
            if(this.entityField?.type?.type !== "List") {
                return false
            }

            if(this.entityField?.type?.referenceField) {
                return true
            }

            if(this.entityField?.type?.entityFieldType?.type ) {
                return this.checkEntityFieldTypeIsReference(this.entityField.type.entityFieldType.type)
            }

            return false
        },
    },
    methods: {
        fetchLabel(key: string) {
            let translationKey: string = `field.${key}`
            if (this.entityField.entityFields
                && this.entityField.entityFields.length > 0) {
                translationKey += ".complex"
            }
            this.subscribeTo(
                this.$translationDataStore.getMessage(translationKey, this.getAppLocale()).pipe(first()),
                translation => {
                    this.fieldLabel = translation
                    this.setStartValue()
                },
                'translation-subscription'
            )
        },

        /**
         * Sets the field value according to its corresponding entityField value
         * Adapts the shape if necesseray, according to entityField type
         */
        setStartValue() {
            // Sometimes in offline mode, the item property of wemi contains also fields from other wemis entities
            // in this case, it is possible that the right value is inside the item and not the other wemi field
            // that is why we have fieldValueSubWemi, fieldValueItem, fieldValueFromRoot
            // see RecordMaskDisplay

            switch (this.entityField.type.type) {
                case "List":
                    this.value = this.fieldValue ??
                        this.fieldValueSubWemi ??
                        this.fieldValueItem ??
                        this.fieldValueFromRoot ??
                        []

                    if(_.isArray(this.value) && this.value.length === 0) {
                        this.addEntry()
                    }
                    break
                default:
                    this.value = this.fieldValue ?? this.fieldValueSubWemi ?? this.fieldValueItem ?? this.fieldValueFromRoot
                    break
            }
        },

        eraseValue() {
            this.$emit(
                "value-change",
                {
                    abstractEntity: this.entityField.abstractEntity,
                    fieldPath: this.entityField.path,
                    value: null,
                } as MaskFieldUpdateEvent
            )
        },

        updateValue(event: MaskFieldUpdateEvent, index: number, entityFieldName: string) {
            let updatedEvent: MaskFieldUpdateEvent = {...event}

            updatedEvent.fieldPath = updatedEvent.fieldPath.replace(
                `${this.entityField.path}`,
                `${this.entityField.path}[${index}]`
            )

            this.reEmitChangeEvent(updatedEvent)
        },

        needToSpan(entityField: EntityField) {
            return (entityField.type.type === "List" || this.singleComplex.includes(entityField.type.type)) && !entityField.isReadOnly;
        },

        // Methods when value is a list
        addEntry() {
            if(this.entityField.type.referenceField && this.entityField.type.entityFieldType?.type) {
                if(this.checkEntityFieldTypeIsReference(this.entityField.type.entityFieldType.type)) {
                    this.value.push('')
                    return
                }
            }

            this.value.push({})
        },

        removeEntry(index: number) {
            const path = `${this.entityField.path}[${index}]`

            this.$emit(
                'value-change',
                {
                    abstractEntity: this.entityField.abstractEntity,
                    fieldPath: path,
                    value: null,
                    type: MaskFieldUpdateEventType.DELETE,
                } as MaskFieldUpdateEvent
            )
        },

        isReadonly(entityField: EntityField) {
            return entityField.isReadOnly || this.getStatus(entityField).disabled
        },

        hasError(entityField: EntityField) {
            return this.getStatus(entityField).error
        },

        infoMessage(entityField: EntityField): string | undefined {
            return this.getStatus(entityField).disabledMessage
        },

        errorMessage(entityField: EntityField): string | undefined {
            return this.getStatus(entityField).errorMessage
        },

        getChildStatus(childEntityField: EntityField): EntityFieldStatus {
            const parentStatus = this.getStatus(this.entityField)
            let childStatus = this.getStatus(childEntityField)
            if(parentStatus.disabled) {
                childStatus.disabled = true
            }
            return childStatus
        },

        getDataCy(index?: number): string {
            let res: string = this.entityField.path
            if(this.dataCy) {
                res = this.dataCy
            }
            if(index !== undefined && index !== null) {
                res = `${res}.${index}`
            }

            return res
        },

        getChildDataCy(childEntityField: EntityField, index?: number) {
            if(index !== undefined && index !== null && index >= 0) {
                return childEntityField.path.replace(
                    this.entityField.path,
                    `${this.getDataCy()}.${index}`
                )
            }

            return childEntityField.path.replace(
                this.entityField.path,
                this.getDataCy()
            )
        },

        getChildFieldValue(childEntityField: EntityField, index?: number) {
            if(!this.value) {
                return null
            }

            let actualPath = this.entityField.path
            let childPath = childEntityField.path

            let pathFromHere = childPath.replace(actualPath, '')
            if(pathFromHere.charAt(0) === '.') {
                pathFromHere = pathFromHere.substring(1)
            }

            if(index !== undefined) {
                return _.get(this.value[index], pathFromHere, null)
            }
            return this.value[pathFromHere] ?? null
        },

        getEntityFieldListEntryValue(entry: any) {
            return _.get(entry, 'ref') ?? entry
        },

        reEmitChangeEvent(event) {
            this.$emit("value-change", event)
        },

        checkEntityFieldTypeIsReference(entityFieldType: string) {
            const lowerCaseEntityFieldType = entityFieldType.toLowerCase()
            const isReference = ENTITY_FIELDS_REFERENCES_TYPES.find(
                type => {
                    return lowerCaseEntityFieldType === type.toLowerCase()
                }
            )

            if(isReference) {
                return true
            }
            return false
        },
    },
    watch: {
        entityField: {
            handler() {
                this.unsubscribeFrom('translation-subscription')
                this.fetchLabel(this.entityField.path)
            },
            immediate: true,
            deep: false,
        },
        '$i18n.locale': {
            handler() {
                this.unsubscribeFrom('translation-subscription')
                this.fetchLabel(this.entityField.path)
            },
            immediate: true,
        },
        fieldValue() {
            this.setStartValue()
        },
    },
})
